import { ref } from 'vue'
import { defineStore } from 'pinia'
import { chatApi } from '../services/chatApi'

export const useChatStore = defineStore('chat', () => {
  const ballPosition = ref({ x: 0, y: 0 })
  const ballVisible = ref(true)
  const isExpanded = ref(false)
  const activeTab = ref('chats')
  const panelPosition = ref({ x: 0, y: 0 })
  const panelSize = ref({ width: 360, height: 480 })
  const loading = ref(false)
  const error = ref(null)

  const pinnedSessions = ref([])
  const recentSessions = ref([])
  const allSessions = ref([])
  const currentSession = ref(null)
  const messages = ref([])

  const recentContacts = ref([])
  const teamContacts = ref([])

  const availableRooms = ref([])
  const activeMeeting = ref(null)

  const myTasks = ref({
    owned: [],
    tracked: [],
    participated: []
  })

  const unreadCount = ref(0)
  const unreadBySession = ref({})

  const loadBallPosition = () => {
    try {
      const saved = localStorage.getItem('chatBallPosition')
      if (saved) {
        ballPosition.value = JSON.parse(saved)
      } else {
        if (typeof window !== 'undefined') {
          ballPosition.value = {
            x: window.innerWidth - 72,
            y: window.innerHeight - 72
          }
        }
      }
    } catch (e) {
      console.warn('loadBallPosition error:', e)
      if (typeof window !== 'undefined') {
        ballPosition.value = {
          x: window.innerWidth - 72,
          y: window.innerHeight - 72
        }
      }
    }
  }

  const saveBallPosition = () => {
    localStorage.setItem('chatBallPosition', JSON.stringify(ballPosition.value))
  }

  const loadPanelSize = () => {
    const saved = localStorage.getItem('chatPanelSize')
    if (saved) {
      const size = JSON.parse(saved)
      panelSize.value = { width: size.width, height: size.height }
      panelPosition.value = { x: size.x, y: size.y }
    }
  }

  const savePanelSize = () => {
    localStorage.setItem('chatPanelSize', JSON.stringify({
      width: panelSize.value.width,
      height: panelSize.value.height,
      x: panelPosition.value.x,
      y: panelPosition.value.y
    }))
  }

  const setBallPosition = (pos) => {
    ballPosition.value = pos
    saveBallPosition()
  }

  const toggleExpanded = () => {
    isExpanded.value = !isExpanded.value
    if (isExpanded.value && !panelPosition.value.x) {
      panelPosition.value = {
        x: ballPosition.value.x - panelSize.value.width / 2 + 24,
        y: ballPosition.value.y - panelSize.value.height - 20
      }
    }
  }

  const closePanel = () => {
    isExpanded.value = false
  }

  const setActiveTab = (tab) => {
    activeTab.value = tab
  }

  const setPanelPosition = (pos) => {
    panelPosition.value = pos
    savePanelSize()
  }

  const setPanelSize = (size) => {
    panelSize.value = size
    savePanelSize()
  }

  const fetchSessions = async () => {
    try {
      loading.value = true
      error.value = null
      const response = await chatApi.getSessions()
      allSessions.value = response.sessions || []
      pinnedSessions.value = response.sessions?.filter(s => s.isPinned) || []
      recentSessions.value = response.sessions?.filter(s => !s.isPinned)
        .sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity)) || []
    } catch (err) {
      error.value = err.response?.data?.message || err.message
      console.error('获取会话列表失败:', err)
    } finally {
      loading.value = false
    }
  }

  const fetchMessages = async (sessionId) => {
    try {
      loading.value = true
      error.value = null
      const response = await chatApi.getMessages(sessionId)
      messages.value = response.messages || []
    } catch (err) {
      error.value = err.response?.data?.message || err.message
      console.error('获取消息失败:', err)
    } finally {
      loading.value = false
    }
  }

  const sendMessage = async (sessionId, content) => {
    try {
      const response = await chatApi.sendMessage(sessionId, content)
      messages.value.push(response.message)
      return response.message
    } catch (err) {
      error.value = err.response?.data?.message || err.message
      throw err
    }
  }

  const fetchContacts = async (teamId) => {
    try {
      loading.value = true
      error.value = null
      const response = await chatApi.getContacts(teamId)
      recentContacts.value = response.recent || []
      teamContacts.value = response.team || []
    } catch (err) {
      error.value = err.response?.data?.message || err.message
      console.error('获取联系人失败:', err)
    } finally {
      loading.value = false
    }
  }

  const fetchMeetingRooms = async () => {
    try {
      loading.value = true
      error.value = null
      const response = await chatApi.getMeetingRooms()
      availableRooms.value = response.rooms || []
    } catch (err) {
      error.value = err.response?.data?.message || err.message
      console.error('获取会议房间失败:', err)
    } finally {
      loading.value = false
    }
  }

  const fetchMyTasks = async () => {
    try {
      loading.value = true
      error.value = null
      const response = await chatApi.getMyTasks()
      myTasks.value = {
        owned: response.owned || [],
        tracked: response.tracked || [],
        participated: response.participated || []
      }
    } catch (err) {
      error.value = err.response?.data?.message || err.message
      console.error('获取任务失败:', err)
    } finally {
      loading.value = false
    }
  }

  const addUnread = (sessionId, count = 1) => {
    unreadBySession.value[sessionId] = (unreadBySession.value[sessionId] || 0) + count
    unreadCount.value += count
  }

  const clearUnread = (sessionId) => {
    if (unreadBySession.value[sessionId]) {
      unreadCount.value -= unreadBySession.value[sessionId]
      unreadBySession.value[sessionId] = 0
    }
  }

  const receiveMessage = (message) => {
    if (currentSession.value?.id === message.sessionId) {
      messages.value.push(message)
    } else {
      addUnread(message.sessionId)
    }
  }

  return {
    ballPosition,
    ballVisible,
    isExpanded,
    activeTab,
    panelPosition,
    panelSize,
    loading,
    error,
    pinnedSessions,
    recentSessions,
    allSessions,
    currentSession,
    messages,
    recentContacts,
    teamContacts,
    availableRooms,
    activeMeeting,
    myTasks,
    unreadCount,
    unreadBySession,
    loadBallPosition,
    saveBallPosition,
    loadPanelSize,
    savePanelSize,
    setBallPosition,
    toggleExpanded,
    closePanel,
    setActiveTab,
    setPanelPosition,
    setPanelSize,
    fetchSessions,
    fetchMessages,
    sendMessage,
    fetchContacts,
    fetchMeetingRooms,
    fetchMyTasks,
    addUnread,
    clearUnread,
    receiveMessage
  }
})
